import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import {
  Page,
  Card,
  Badge as PolarisBadge,
  Button as PolarisButton,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  DataTable as PolarisDataTable,
  Spinner,
  TextField,
} from "@shopify/polaris";
import UserSelect from "features/user-select/user-select";
import useIsPhone from "features/files/use-is-phone";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchIncidentById,
  generateIncidentAct,
  cancelIncidentSigning,
  setIncidentStatus,
  setIncidentArchived,
  createIncidentAction,
  updateIncidentAction,
  deleteIncidentAction,
  IncidentAction,
  IncidentDetailResult,
} from "services/work-security-incidents.service";
import {
  incidentTypeLabel,
  incidentSeverityLabel,
  incidentStatusLabel,
} from "containers/work-security-incidents-container/work-security-incidents-panel";
import CorrectiveActionCards from "./corrective-action-cards";

function fmtDate(ms: number | null): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}
function fmtDateTime(ms: number | null): string {
  if (!ms) return "—";
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${fmtDate(ms)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function Field({ label, value }: { label: string; value?: React.ReactNode }) {
  return (
    <div className="py-2 border-b border-zinc-100 dark:border-zinc-800">
      <Text as="p" variant="bodySm" tone="subdued">
        {label}
      </Text>
      <Text as="p" variant="bodyMd">
        {value === undefined || value === null || value === "" ? "—" : value}
      </Text>
    </div>
  );
}

export default function WorkSecurityIncidentViewContainer() {
  const { companyId, incidentId } = useParams<{
    companyId: string;
    incidentId: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const isPhone = useIsPhone();

  const [data, setData] = useState<IncidentDetailResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const requestRef = useRef(0);
  // Tracks whether we already have an incident loaded, so a failed *refresh*
  // (e.g. after a successful mutation) keeps the current view instead of
  // replacing the whole page with the load-failed screen.
  const dataRef = useRef(false);
  const [actionError, setActionError] = useState("");

  // Inline "add corrective action" form
  const [newActionDesc, setNewActionDesc] = useState("");
  const [newActionOwner, setNewActionOwner] = useState("");
  const [newActionDue, setNewActionDue] = useState("");

  const load = useCallback(async () => {
    if (!companyId || !incidentId) return;
    const requestId = ++requestRef.current;
    try {
      setLoading(true);
      const res = await fetchIncidentById(companyId, incidentId);
      if (requestId !== requestRef.current) return;
      setData(res);
      dataRef.current = true;
      setLoadError(false);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      console.error("Failed to load incident", err);
      // Only surface the blocking load-failed screen on the initial load. If an
      // incident is already on screen, a failed refresh leaves it in place.
      if (!dataRef.current) setLoadError(true);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [companyId, incidentId]);

  useEffect(() => {
    // Switching incident (or company): drop the previous incident so its data
    // can never be shown for — or acted on under — the new URL, and re-arm the
    // initial-load error screen (dataRef gates that). Manual refreshes call
    // load() directly and intentionally skip this reset.
    dataRef.current = false;
    setData(null);
    setLoadError(false);
    load();
  }, [load]);

  const runAction = async (fn: () => Promise<any>): Promise<boolean> => {
    if (!companyId || !incidentId || busyRef.current) return false;
    setActionError("");
    busyRef.current = true;
    setBusy(true);
    try {
      await fn();
      await load();
      return true;
    } catch (err: any) {
      setActionError(
        err?.response?.data?.message ||
          t("incident_action_failed", {
            defaultValue: "Action failed. Please try again.",
          }),
      );
      return false;
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const openEditModal = () => {
    if (!data) return;
    dispatch(
      setModalxOptions({
        title: t("edit_incident", { defaultValue: "Edit Incident" }),
      }),
    );
    dispatch(setModalxComponent("IncidentFormModal"));
    dispatch(
      setModalxComponentProps({
        incident: data.incident,
        onSuccess: () => load(),
      }),
    );
  };

  const openSendModal = () => {
    if (!data) return;
    dispatch(
      setModalxOptions({
        title: t("send_incident_for_signature", {
          defaultValue: "Send for Signature",
        }),
      }),
    );
    dispatch(setModalxComponent("IncidentSendForSignatureModal"));
    dispatch(
      setModalxComponentProps({
        incidentId: data.incident.id,
        victimUser: data.incident.victimUser,
        defaultManagerUser: data.incident.managerUser,
        onSuccess: () => load(),
      }),
    );
  };

  const addAction = async () => {
    if (!companyId || !incidentId || !newActionDesc.trim()) return;
    // Date input is YYYY-MM-DD; append T00:00:00 so `new Date` parses local midnight
    // (bare date strings are UTC). Matches occurredAt epoch handling for display symmetry.
    const dueDateMs = newActionDue
      ? new Date(`${newActionDue}T00:00:00`).getTime()
      : null;
    const ok = await runAction(() =>
      createIncidentAction(companyId, incidentId, {
        description: newActionDesc.trim(),
        owner: newActionOwner || null,
        dueDate: dueDateMs,
      }),
    );
    if (ok) {
      setNewActionDesc("");
      setNewActionOwner("");
      setNewActionDue("");
    }
  };

  if (loading && !data) {
    return (
      <Page
        title={t("incident", { defaultValue: "Incident" })}
        backAction={{
          onAction: () => navigate(`/app/${companyId}/work-security/incidents`),
        }}
      >
        <div className="flex justify-center py-20">
          <Spinner accessibilityLabel="Loading" size="large" />
        </div>
      </Page>
    );
  }

  if (loadError || !data) {
    return (
      <Page
        title={t("incident", { defaultValue: "Incident" })}
        backAction={{
          onAction: () => navigate(`/app/${companyId}/work-security/incidents`),
        }}
      >
        <Banner tone="critical">
          {t("incident_load_failed", {
            defaultValue: "Unable to load this incident.",
          })}
        </Banner>
      </Page>
    );
  }

  const inc = data.incident;
  // Signatures on the CURRENT (newest) act, from the backend — not documents.find,
  // which could match a stale older signed act once regeneration keeps signed
  // acts as history.
  const actSignedCount = inc.currentActSignedCount;
  // Closed/cancelled/archived incidents must not gain or mutate CAPA items —
  // close is gated on clearing open actions, so keep that invariant after close.
  const actionsMutable =
    !inc.isArchived && inc.status !== "cancelled" && inc.status !== "closed";

  const secondaryActions: any[] = [];
  if (inc.status === "registered" && !inc.isArchived) {
    secondaryActions.push({
      content: t("edit", { defaultValue: "Edit" }),
      onAction: openEditModal,
      disabled: busy,
    });
  }
  if (
    !inc.isSevere &&
    !inc.isArchived &&
    ["registered", "signed"].includes(inc.status)
  ) {
    secondaryActions.push({
      content: inc.hasCurrentAct
        ? t("regenerate_act", { defaultValue: "Regenerate act" })
        : t("generate_act", { defaultValue: "Generate act" }),
      onAction: () =>
        runAction(() => generateIncidentAct(companyId!, incidentId!)),
      disabled: busy,
    });
  }
  if (
    !inc.isSevere &&
    !inc.isArchived &&
    inc.hasCurrentAct &&
    !inc.currentActSigned &&
    ["registered", "signed"].includes(inc.status)
  ) {
    // Allow re-send after regenerating a previously signed act (status may stay
    // "signed" while the new current act is unsigned).
    secondaryActions.push({
      content: t("send_for_signature", { defaultValue: "Send for signature" }),
      onAction: openSendModal,
      disabled: busy,
    });
  }
  if (
    !inc.isArchived &&
    inc.status === "pending_signatures" &&
    actSignedCount === 0
  ) {
    // Once anyone has signed, the backend blocks cancel-signing (INCIDENT_ALREADY_SIGNED);
    // don't offer an action that would only 400. Archived incidents are immutable.
    secondaryActions.push({
      content: t("cancel_signing", { defaultValue: "Cancel signing" }),
      onAction: () =>
        runAction(() => cancelIncidentSigning(companyId!, incidentId!)),
      disabled: busy,
    });
  }
  if (inc.canClose && !inc.isArchived) {
    // Archived incidents are immutable — same freeze as edit/generate/send/reopen.
    secondaryActions.push({
      content: t("close_incident", { defaultValue: "Close incident" }),
      onAction: () =>
        runAction(() => setIncidentStatus(companyId!, incidentId!, "close")),
      disabled: busy,
    });
  }
  if (!inc.isArchived && ["closed", "cancelled"].includes(inc.status)) {
    secondaryActions.push({
      content: t("reopen_incident", { defaultValue: "Reopen" }),
      onAction: () =>
        runAction(() => setIncidentStatus(companyId!, incidentId!, "reopen")),
      disabled: busy,
    });
  }
  secondaryActions.push({
    content: inc.isArchived
      ? t("restore", { defaultValue: "Restore" })
      : t("archive", { defaultValue: "Archive" }),
    onAction: () =>
      runAction(() =>
        setIncidentArchived(companyId!, incidentId!, !inc.isArchived),
      ),
    disabled: busy,
  });

  const toggleActionDone = (action: IncidentAction) =>
    runAction(() =>
      updateIncidentAction(companyId!, incidentId!, action.id, {
        done: !action.doneAt,
      }),
    );

  const deleteAction = (action: IncidentAction) => {
    const confirmed = window.confirm(
      t("incident_action_delete_confirm", {
        defaultValue: "Delete this corrective action?",
      }),
    );
    if (!confirmed) return;
    runAction(() => deleteIncidentAction(companyId!, incidentId!, action.id));
  };

  const actionRows = data.actions.map((a) => [
    a.description,
    a.ownerName || "—",
    a.dueDate ? fmtDate(a.dueDate) : "—",
    a.doneAt ? (
      <PolarisBadge tone="success">
        {t("done", { defaultValue: "Done" })}
      </PolarisBadge>
    ) : (
      <PolarisBadge tone="attention">
        {t("open", { defaultValue: "Open" })}
      </PolarisBadge>
    ),
    <InlineStack gap="200" key={a.id}>
      <PolarisButton
        variant="plain"
        disabled={busy || !actionsMutable}
        onClick={() => toggleActionDone(a)}
      >
        {a.doneAt
          ? t("mark_open", { defaultValue: "Mark open" })
          : t("mark_done", { defaultValue: "Mark done" })}
      </PolarisButton>
      <PolarisButton
        variant="plain"
        tone="critical"
        disabled={busy || !actionsMutable}
        onClick={() => deleteAction(a)}
      >
        {t("delete", { defaultValue: "Delete" })}
      </PolarisButton>
    </InlineStack>,
  ]);

  return (
    <Page
      fullWidth
      title={inc.number || t("incident", { defaultValue: "Incident" })}
      backAction={{
        onAction: () => navigate(`/app/${companyId}/work-security/incidents`),
      }}
      titleMetadata={
        <InlineStack gap="200">
          <PolarisBadge>{incidentTypeLabel(t, inc.type)}</PolarisBadge>
          {inc.severity !== "none" && (
            <PolarisBadge tone={inc.isSevere ? "critical" : "attention"}>
              {incidentSeverityLabel(t, inc.severity)}
            </PolarisBadge>
          )}
          <PolarisBadge
            tone={
              inc.status === "signed"
                ? "success"
                : inc.status === "pending_signatures"
                  ? "attention"
                  : inc.status === "cancelled"
                    ? "critical"
                    : undefined
            }
          >
            {incidentStatusLabel(t, inc.status)}
          </PolarisBadge>
          {inc.isArchived && (
            <PolarisBadge>
              {t("status_archived", { defaultValue: "Archived" })}
            </PolarisBadge>
          )}
        </InlineStack>
      }
      secondaryActions={secondaryActions}
    >
      <BlockStack gap="400">
        {actionError && (
          <Banner tone="critical" onDismiss={() => setActionError("")}>
            {actionError}
          </Banner>
        )}

        {inc.isSevere && (
          <Banner tone="critical">
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              {t("incident_severe_banner_title", {
                defaultValue: "Serious/fatal accident — VDI investigates",
              })}
            </Text>
            <Text as="p" variant="bodySm">
              {t("incident_severe_banner_body", {
                defaultValue:
                  "Notify the prosecutor's office, the VDI territorial unit and the family immediately, preserve the scene, and submit the accident notification (Pranešimas apie įvykį darbe). The company must not draw up its own act — attach VDI's act as a file instead.",
              })}
            </Text>
          </Banner>
        )}

        {inc.deadline?.isOverdue &&
          !inc.isSevere &&
          !["signed", "closed", "cancelled"].includes(inc.status) && (
            <Banner tone="warning">
              {t("incident_deadline_overdue_banner", {
                defaultValue:
                  "The 10-working-day investigation deadline for a light accident has passed.",
              })}{" "}
              {t("incident_deadline_due", { defaultValue: "Due" })}:{" "}
              {fmtDate(inc.deadline.deadlineAt)}
            </Banner>
          )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card roundedAbove="sm">
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                {t("incident_general_info", {
                  defaultValue: "General information",
                })}
              </Text>
              <Field
                label={t("incident_occurred_at", {
                  defaultValue: "Date and time",
                })}
                value={fmtDateTime(inc.occurredAt)}
              />
              <Field
                label={t("incident_time_since_shift", {
                  defaultValue: "Time since shift start",
                })}
                value={inc.timeSinceShiftStart}
              />
              <Field
                label={t("incident_location", {
                  defaultValue: "Location / unit",
                })}
                value={inc.location}
              />
              <Field
                label={t("incident_victim", {
                  defaultValue: "Affected employee",
                })}
                value={
                  <>
                    {inc.victimName}
                    {inc.victimSnapshot?.position
                      ? ` · ${inc.victimSnapshot.position}`
                      : ""}
                  </>
                }
              />
              {inc.deadline && (
                <Field
                  label={t("incident_investigation_deadline", {
                    defaultValue: "Investigation deadline",
                  })}
                  value={fmtDate(inc.deadline.deadlineAt)}
                />
              )}
            </BlockStack>
          </Card>

          <Card roundedAbove="sm">
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                {t("incident_circumstances", { defaultValue: "Circumstances" })}
              </Text>
              <Field
                label={t("incident_activity_before", {
                  defaultValue: "Activity before the event",
                })}
                value={inc.activityBefore}
              />
              <Field
                label={t("incident_description", {
                  defaultValue: "What happened",
                })}
                value={inc.description}
              />
              <Field
                label={t("incident_causing_factor", {
                  defaultValue: "Causing factor / object",
                })}
                value={inc.causingFactor}
              />
              {inc.type === "event_at_work" && (
                <>
                  <Field
                    label={t("incident_injury_nature", {
                      defaultValue: "Injury nature",
                    })}
                    value={inc.injuryNature}
                  />
                  <Field
                    label={t("incident_body_part", {
                      defaultValue: "Injured body part",
                    })}
                    value={inc.bodyPart}
                  />
                </>
              )}
            </BlockStack>
          </Card>
        </div>

        {inc.witnesses.length > 0 && (
          <Card roundedAbove="sm">
            <BlockStack gap="200">
              <Text as="h3" variant="headingSm">
                {t("incident_witnesses", { defaultValue: "Witnesses" })}
              </Text>
              {inc.witnesses.map((w, i) => (
                <div
                  key={i}
                  className="py-1 border-b border-zinc-100 dark:border-zinc-800"
                >
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {w.name}
                  </Text>
                  {w.notes && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {w.notes}
                    </Text>
                  )}
                </div>
              ))}
            </BlockStack>
          </Card>
        )}

        <Card roundedAbove="sm">
          <BlockStack gap="200">
            <Text as="h3" variant="headingSm">
              {t("incident_causes_prevention", {
                defaultValue: "Causes and preventive measures",
              })}
            </Text>
            <Field
              label={t("incident_causes", { defaultValue: "Causes" })}
              value={inc.causes}
            />
            <Field
              label={t("incident_prevention", {
                defaultValue: "Preventive measures",
              })}
              value={inc.preventiveMeasures}
            />
          </BlockStack>
        </Card>

        {/* Corrective actions (CAPA-lite) */}
        <Card roundedAbove="sm">
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingSm">
                {t("incident_actions", { defaultValue: "Corrective actions" })}
              </Text>
              {inc.openActionsCount > 0 && (
                <PolarisBadge tone="attention">
                  {t("incident_open_actions", {
                    defaultValue: "{{count}} open",
                    count: inc.openActionsCount,
                  })}
                </PolarisBadge>
              )}
            </InlineStack>

            {data.actions.length > 0 ? (
              isPhone ? (
                <CorrectiveActionCards
                  actions={data.actions}
                  disabled={busy || !actionsMutable}
                  formatDate={fmtDate}
                  onToggleDone={toggleActionDone}
                  onDelete={deleteAction}
                />
              ) : (
                <PolarisDataTable
                  columnContentTypes={["text", "text", "text", "text", "text"]}
                  headings={[
                    t("incident_action_description", { defaultValue: "Action" }),
                    t("incident_action_owner", { defaultValue: "Owner" }),
                    t("incident_action_due", { defaultValue: "Due" }),
                    t("status", { defaultValue: "Status" }),
                    "",
                  ]}
                  rows={actionRows}
                />
              )
            ) : (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("incident_no_actions", {
                  defaultValue: "No corrective actions yet.",
                })}
              </Text>
            )}

            {actionsMutable && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end border-t border-zinc-100 dark:border-zinc-800 pt-3">
                <div className="md:col-span-2">
                  <TextField
                    label={t("incident_action_description", {
                      defaultValue: "Action",
                    })}
                    value={newActionDesc}
                    onChange={setNewActionDesc}
                    autoComplete="off"
                    placeholder={t("incident_action_placeholder", {
                      defaultValue: "e.g. Provide additional training",
                    })}
                  />
                </div>
                <UserSelect
                  value={newActionOwner}
                  onChange={(v: string) => setNewActionOwner(v)}
                  label="incident_action_owner"
                  showUserEmail
                />
                <div className="flex gap-2 items-end">
                  <TextField
                    label={t("incident_action_due", {
                      defaultValue: "Due date",
                    })}
                    type="date"
                    value={newActionDue}
                    onChange={setNewActionDue}
                    autoComplete="off"
                  />
                  <PolarisButton
                    onClick={addAction}
                    disabled={!newActionDesc.trim() || busy}
                  >
                    {t("add", { defaultValue: "Add" })}
                  </PolarisButton>
                </div>
              </div>
            )}
          </BlockStack>
        </Card>

        {/* Documents */}
        <Card roundedAbove="sm">
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              {t("incident_documents", { defaultValue: "Documents" })}
            </Text>
            {data.documents.length === 0 ? (
              <Text as="p" variant="bodySm" tone="subdued">
                {t("incident_no_documents", {
                  defaultValue: "No act generated yet.",
                })}
              </Text>
            ) : (
              data.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="border border-zinc-200 dark:border-zinc-800 rounded-lg p-3"
                >
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {doc.name}{" "}
                        <PolarisBadge>
                          {doc.docType === "act"
                            ? t("incident_doc_act", { defaultValue: "Act" })
                            : t("incident_doc_attachment", {
                                defaultValue: "Attachment",
                              })}
                        </PolarisBadge>
                      </Text>
                      {doc.docType === "act" && (
                        <PolarisBadge
                          tone={doc.isSigned ? "success" : "attention"}
                        >
                          {doc.isSigned
                            ? t("signed", { defaultValue: "Signed" })
                            : `${doc.signedCount}/${doc.totalCount} ${t(
                                "signed",
                                {
                                  defaultValue: "signed",
                                },
                              )}`}
                        </PolarisBadge>
                      )}
                    </BlockStack>
                    <PolarisButton
                      onClick={() =>
                        navigate(`/app/${companyId}/documents/view/${doc.fileId}`)
                      }
                    >
                      {t("view", { defaultValue: "View" })}
                    </PolarisButton>
                  </InlineStack>
                  {doc.signers.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {doc.signers.map((s) => (
                        <PolarisBadge
                          key={s.userId}
                          tone={s.status === "signed" ? "success" : "attention"}
                        >
                          {`${s.name}: ${
                            s.status === "signed"
                              ? t("signed", { defaultValue: "signed" })
                              : t("pending", { defaultValue: "pending" })
                          }`}
                        </PolarisBadge>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
